/**
 * Paises del mundo: codigo ISO 3166-1 alfa-2, prefijo telefonico y moneda.
 *
 * Por que existe. Hasta el 7 de septiembre de 2026 el alta ofrecia siete
 * paises y un «Otro pais»: una trattoria italiana se daba de alta como
 * «Otro pais · USD» y ni sabiamos donde estaba ni podiamos llamarla. Ahora
 * el pais es siempre uno de verdad y el WhatsApp lleva siempre su prefijo.
 *
 * Los NOMBRES no estan aqui: los da `Intl.DisplayNames` en espanyol, que
 * lo tienen el navegador y Node (v26 en la API). Asi la tabla es corta y no
 * hay que traducir nada.
 *
 * El orden importa para los prefijos compartidos (+1, +7, +44, +47, +61,
 * +212, +262, +590, +599): al partir un numero, empata el primero.
 */

export interface Pais {
  /** ISO 3166-1 alfa-2, en mayusculas. */
  code: string;
  /** Prefijo telefonico internacional, con el «+». */
  dial: string;
  /** Otros prefijos del mismo pais (Dominicana tiene tres). */
  alt?: readonly string[];
  /** Moneda ISO 4217 con la que se lleva el negocio alli. */
  currency: string;
}

const P = (code: string, dial: string, currency: string, alt?: readonly string[]): Pais =>
  alt ? { code, dial, currency, alt } : { code, dial, currency };

export const PAISES: readonly Pais[] = [
  /* --- Los que tienen preset propio primero: son la mayoria de clientes --- */
  P('ES', '+34', 'EUR'),
  P('MX', '+52', 'MXN'),
  P('CO', '+57', 'COP'),
  P('AR', '+54', 'ARS'),
  P('CL', '+56', 'CLP'),
  P('PE', '+51', 'PEN'),
  P('US', '+1', 'USD'),
  /* --- Resto de America --- */
  P('CA', '+1', 'CAD'),
  P('BO', '+591', 'BOB'),
  P('BR', '+55', 'BRL'),
  P('CR', '+506', 'CRC'),
  P('CU', '+53', 'CUP'),
  P('DO', '+1809', 'DOP', ['+1829', '+1849']),
  P('EC', '+593', 'USD'),
  P('SV', '+503', 'USD'),
  P('GT', '+502', 'GTQ'),
  P('HN', '+504', 'HNL'),
  P('NI', '+505', 'NIO'),
  P('PA', '+507', 'PAB'),
  P('PY', '+595', 'PYG'),
  P('UY', '+598', 'UYU'),
  P('VE', '+58', 'VES'),
  P('PR', '+1787', 'USD', ['+1939']),
  P('AG', '+1268', 'XCD'),
  P('AI', '+1264', 'XCD'),
  P('AW', '+297', 'AWG'),
  P('BB', '+1246', 'BBD'),
  P('BM', '+1441', 'BMD'),
  P('BS', '+1242', 'BSD'),
  P('BZ', '+501', 'BZD'),
  P('CW', '+599', 'XCG'),
  P('BQ', '+599', 'USD'),
  P('DM', '+1767', 'XCD'),
  P('GD', '+1473', 'XCD'),
  P('GF', '+594', 'EUR'),
  P('GP', '+590', 'EUR'),
  P('BL', '+590', 'EUR'),
  P('MF', '+590', 'EUR'),
  P('GY', '+592', 'GYD'),
  P('HT', '+509', 'HTG'),
  P('JM', '+1876', 'JMD', ['+1658']),
  P('KN', '+1869', 'XCD'),
  P('KY', '+1345', 'KYD'),
  P('LC', '+1758', 'XCD'),
  P('MQ', '+596', 'EUR'),
  P('MS', '+1664', 'XCD'),
  P('SR', '+597', 'SRD'),
  P('SX', '+1721', 'XCG'),
  P('TC', '+1649', 'USD'),
  P('TT', '+1868', 'TTD'),
  P('VC', '+1784', 'XCD'),
  P('VG', '+1284', 'USD'),
  P('VI', '+1340', 'USD'),
  P('FK', '+500', 'FKP'),
  P('GL', '+299', 'DKK'),
  P('PM', '+508', 'EUR'),
  /* --- Europa --- */
  P('AD', '+376', 'EUR'),
  P('AL', '+355', 'ALL'),
  P('AT', '+43', 'EUR'),
  P('BA', '+387', 'BAM'),
  P('BE', '+32', 'EUR'),
  P('BG', '+359', 'EUR'),
  P('BY', '+375', 'BYN'),
  P('CH', '+41', 'CHF'),
  P('CY', '+357', 'EUR'),
  P('CZ', '+420', 'CZK'),
  P('DE', '+49', 'EUR'),
  P('DK', '+45', 'DKK'),
  P('EE', '+372', 'EUR'),
  P('FI', '+358', 'EUR'),
  P('AX', '+358', 'EUR'),
  P('FO', '+298', 'DKK'),
  P('FR', '+33', 'EUR'),
  P('GB', '+44', 'GBP'),
  P('GG', '+44', 'GBP'),
  P('IM', '+44', 'GBP'),
  P('JE', '+44', 'GBP'),
  P('GI', '+350', 'GIP'),
  P('GR', '+30', 'EUR'),
  P('HR', '+385', 'EUR'),
  P('HU', '+36', 'HUF'),
  P('IE', '+353', 'EUR'),
  P('IS', '+354', 'ISK'),
  P('IT', '+39', 'EUR'),
  P('VA', '+39', 'EUR'),
  P('LI', '+423', 'CHF'),
  P('LT', '+370', 'EUR'),
  P('LU', '+352', 'EUR'),
  P('LV', '+371', 'EUR'),
  P('MC', '+377', 'EUR'),
  P('MD', '+373', 'MDL'),
  P('ME', '+382', 'EUR'),
  P('MK', '+389', 'MKD'),
  P('MT', '+356', 'EUR'),
  P('NL', '+31', 'EUR'),
  P('NO', '+47', 'NOK'),
  P('SJ', '+47', 'NOK'),
  P('PL', '+48', 'PLN'),
  P('PT', '+351', 'EUR'),
  P('RO', '+40', 'RON'),
  P('RS', '+381', 'RSD'),
  P('RU', '+7', 'RUB'),
  P('KZ', '+7', 'KZT', ['+76', '+77']),
  P('SE', '+46', 'SEK'),
  P('SI', '+386', 'EUR'),
  P('SK', '+421', 'EUR'),
  P('SM', '+378', 'EUR'),
  P('UA', '+380', 'UAH'),
  /* --- Africa --- */
  P('DZ', '+213', 'DZD'),
  P('AO', '+244', 'AOA'),
  P('BJ', '+229', 'XOF'),
  P('BW', '+267', 'BWP'),
  P('BF', '+226', 'XOF'),
  P('BI', '+257', 'BIF'),
  P('CM', '+237', 'XAF'),
  P('CV', '+238', 'CVE'),
  P('CF', '+236', 'XAF'),
  P('TD', '+235', 'XAF'),
  P('KM', '+269', 'KMF'),
  P('CG', '+242', 'XAF'),
  P('CD', '+243', 'CDF'),
  P('CI', '+225', 'XOF'),
  P('DJ', '+253', 'DJF'),
  P('EG', '+20', 'EGP'),
  P('GQ', '+240', 'XAF'),
  P('ER', '+291', 'ERN'),
  P('SZ', '+268', 'SZL'),
  P('ET', '+251', 'ETB'),
  P('GA', '+241', 'XAF'),
  P('GM', '+220', 'GMD'),
  P('GH', '+233', 'GHS'),
  P('GN', '+224', 'GNF'),
  P('GW', '+245', 'XOF'),
  P('KE', '+254', 'KES'),
  P('LS', '+266', 'LSL'),
  P('LR', '+231', 'LRD'),
  P('LY', '+218', 'LYD'),
  P('MG', '+261', 'MGA'),
  P('MW', '+265', 'MWK'),
  P('ML', '+223', 'XOF'),
  P('MR', '+222', 'MRU'),
  P('MU', '+230', 'MUR'),
  P('MA', '+212', 'MAD'),
  P('EH', '+212', 'MAD'),
  P('MZ', '+258', 'MZN'),
  P('NA', '+264', 'NAD'),
  P('NE', '+227', 'XOF'),
  P('NG', '+234', 'NGN'),
  P('RE', '+262', 'EUR'),
  P('YT', '+262', 'EUR'),
  P('RW', '+250', 'RWF'),
  P('SH', '+290', 'SHP'),
  P('ST', '+239', 'STN'),
  P('SN', '+221', 'XOF'),
  P('SC', '+248', 'SCR'),
  P('SL', '+232', 'SLE'),
  P('SO', '+252', 'SOS'),
  P('ZA', '+27', 'ZAR'),
  P('SS', '+211', 'SSP'),
  P('SD', '+249', 'SDG'),
  P('TZ', '+255', 'TZS'),
  P('TG', '+228', 'XOF'),
  P('TN', '+216', 'TND'),
  P('UG', '+256', 'UGX'),
  P('ZM', '+260', 'ZMW'),
  P('ZW', '+263', 'USD'),
  /* --- Asia y Oriente Medio --- */
  P('AF', '+93', 'AFN'),
  P('AM', '+374', 'AMD'),
  P('AZ', '+994', 'AZN'),
  P('BH', '+973', 'BHD'),
  P('BD', '+880', 'BDT'),
  P('BT', '+975', 'BTN'),
  P('BN', '+673', 'BND'),
  P('KH', '+855', 'KHR'),
  P('CN', '+86', 'CNY'),
  P('GE', '+995', 'GEL'),
  P('HK', '+852', 'HKD'),
  P('IN', '+91', 'INR'),
  P('ID', '+62', 'IDR'),
  P('IR', '+98', 'IRR'),
  P('IQ', '+964', 'IQD'),
  P('IL', '+972', 'ILS'),
  P('PS', '+970', 'ILS'),
  P('JP', '+81', 'JPY'),
  P('JO', '+962', 'JOD'),
  P('KW', '+965', 'KWD'),
  P('KG', '+996', 'KGS'),
  P('LA', '+856', 'LAK'),
  P('LB', '+961', 'LBP'),
  P('MO', '+853', 'MOP'),
  P('MY', '+60', 'MYR'),
  P('MV', '+960', 'MVR'),
  P('MN', '+976', 'MNT'),
  P('MM', '+95', 'MMK'),
  P('NP', '+977', 'NPR'),
  P('KP', '+850', 'KPW'),
  P('OM', '+968', 'OMR'),
  P('PK', '+92', 'PKR'),
  P('PH', '+63', 'PHP'),
  P('QA', '+974', 'QAR'),
  P('SA', '+966', 'SAR'),
  P('SG', '+65', 'SGD'),
  P('KR', '+82', 'KRW'),
  P('LK', '+94', 'LKR'),
  P('SY', '+963', 'SYP'),
  P('TW', '+886', 'TWD'),
  P('TJ', '+992', 'TJS'),
  P('TH', '+66', 'THB'),
  P('TL', '+670', 'USD'),
  P('TR', '+90', 'TRY'),
  P('TM', '+993', 'TMT'),
  P('AE', '+971', 'AED'),
  P('UZ', '+998', 'UZS'),
  P('VN', '+84', 'VND'),
  P('YE', '+967', 'YER'),
  P('IO', '+246', 'USD'),
  /* --- Oceania --- */
  P('AU', '+61', 'AUD'),
  P('CX', '+61', 'AUD'),
  P('CC', '+61', 'AUD'),
  P('NZ', '+64', 'NZD'),
  P('PN', '+64', 'NZD'),
  P('FJ', '+679', 'FJD'),
  P('PG', '+675', 'PGK'),
  P('SB', '+677', 'SBD'),
  P('VU', '+678', 'VUV'),
  P('WS', '+685', 'WST'),
  P('TO', '+676', 'TOP'),
  P('KI', '+686', 'AUD'),
  P('NR', '+674', 'AUD'),
  P('TV', '+688', 'AUD'),
  P('FM', '+691', 'USD'),
  P('MH', '+692', 'USD'),
  P('PW', '+680', 'USD'),
  P('CK', '+682', 'NZD'),
  P('NU', '+683', 'NZD'),
  P('TK', '+690', 'NZD'),
  P('PF', '+689', 'XPF'),
  P('NC', '+687', 'XPF'),
  P('WF', '+681', 'XPF'),
  P('GU', '+1671', 'USD'),
  P('MP', '+1670', 'USD'),
  P('AS', '+1684', 'USD'),
  P('NF', '+672', 'AUD'),
];

const POR_CODIGO: ReadonlyMap<string, Pais> = new Map(PAISES.map((p) => [p.code, p]));

/** Valor que guardaban los tenants antiguos cuando su pais no estaba en la lista. */
export const PAIS_SIN_INDICAR = 'OTHER';

const limpiarCodigo = (code: string | undefined): string => (code ?? '').trim().toUpperCase();

export function paisDe(code: string | undefined): Pais | undefined {
  return POR_CODIGO.get(limpiarCodigo(code));
}

/** true si es un ISO alfa-2 de la tabla (no vale «OTHER»). */
export function esPaisConocido(code: string | undefined): boolean {
  return POR_CODIGO.has(limpiarCodigo(code));
}

/** «ES» → «+34». Vacio si el pais no esta en la tabla. */
export function prefijoDe(code: string | undefined): string {
  return paisDe(code)?.dial ?? '';
}

/** «MX» → «MXN». `porDefecto` si el pais no esta en la tabla. */
export function monedaDe(code: string | undefined, porDefecto = 'USD'): string {
  return paisDe(code)?.currency ?? porDefecto;
}

/*
 * Cache de nombres: `Intl.DisplayNames` se construye una vez por locale y el
 * nombre de cada pais se guarda, porque la lista del alta lo pide 240 veces
 * al ordenar.
 */
const NOMBRES = new Map<string, string | undefined>();
let displayNames: Intl.DisplayNames | undefined;

/** «MX» → «México». Si el codigo no es un pais («OTHER», vacio...), nada. */
export function nombrePais(code: string | undefined): string | undefined {
  const c = limpiarCodigo(code);
  if (!/^[A-Z]{2}$/.test(c)) return undefined;
  if (NOMBRES.has(c)) return NOMBRES.get(c);
  let nombre: string | undefined;
  try {
    displayNames ??= new Intl.DisplayNames(['es'], { type: 'region' });
    const n = displayNames.of(c);
    nombre = n && n !== c ? n : undefined;
  } catch {
    nombre = undefined;
  }
  NOMBRES.set(c, nombre);
  return nombre;
}

/**
 * Nombre para ensenyar SIEMPRE algo: «México», «País sin indicar» para los
 * tenants antiguos con «OTHER», y el codigo tal cual si no lo conocemos.
 */
export function etiquetaPais(code: string | undefined): string {
  const c = limpiarCodigo(code);
  if (c === '' || c === PAIS_SIN_INDICAR) return 'País sin indicar';
  return nombrePais(c) ?? c;
}

/** «ES» → 🇪🇸 (indicadores regionales). Vacio si no es un alfa-2. */
export function banderaDe(code: string | undefined): string {
  const c = limpiarCodigo(code);
  if (!/^[A-Z]{2}$/.test(c)) return '';
  return String.fromCodePoint(...[...c].map((ch) => 0x1f1e6 + ch.charCodeAt(0) - 65));
}

/** Todos los paises ordenados por su nombre en espanyol. */
export function paisesOrdenados(): Pais[] {
  return [...PAISES].sort((a, b) =>
    (nombrePais(a.code) ?? a.code).localeCompare(nombrePais(b.code) ?? b.code, 'es'),
  );
}

/* ------------------------------------------------------------------ */
/* Telefonos                                                           */
/* ------------------------------------------------------------------ */

/**
 * Deja un telefono en E.164 («+34600123456»): sin espacios, guiones ni
 * parentesis, con el «+» delante (un «00» internacional se convierte).
 *
 * - vacio → «» (el que llama decide si eso es borrar);
 * - sin prefijo o con letras → null. El prefijo es obligatorio porque un
 *   «600 123 456» sin pais no se puede marcar desde fuera.
 */
export function normalizarTelefono(valor: string): string | null {
  let limpio = valor.trim().replace(/[\s().-]/g, '');
  if (limpio === '') return '';
  if (/^00\d/.test(limpio)) limpio = `+${limpio.slice(2)}`;
  return /^\+\d{7,15}$/.test(limpio) ? limpio : null;
}

/** true si al numero le falta el «+» y el prefijo del pais. */
export function telefonoSinPrefijo(valor: string): boolean {
  const limpio = valor.trim().replace(/[\s().-]/g, '');
  return /^\d{6,}$/.test(limpio);
}

export interface TelefonoPartido {
  /** Pais del prefijo, si se reconoce. */
  country: string | undefined;
  /** Prefijo reconocido («+34») o vacio. */
  dial: string;
  /** Lo que va detras del prefijo, solo cifras. */
  national: string;
}

/**
 * Separa prefijo y numero nacional. Con prefijos compartidos (+1 es Estados
 * Unidos y Canada, +44 Reino Unido y sus islas) gana `paisPreferido` si
 * encaja y, si no, el primero de la tabla, que es el pais grande.
 *
 * Un numero sin «+» (dato antiguo, tecleado sin prefijo) se devuelve entero
 * como nacional y con el pais preferido, para que la pantalla lo proponga.
 */
export function partirTelefono(valor: string, paisPreferido?: string): TelefonoPartido {
  const limpio = (valor ?? '').trim().replace(/[\s().-]/g, '');
  const preferido = limpiarCodigo(paisPreferido);
  if (!limpio.startsWith('+')) {
    return {
      country: esPaisConocido(preferido) ? preferido : undefined,
      dial: '',
      national: limpio.replace(/\D/g, ''),
    };
  }
  let mejorLargo = 0;
  let candidatos: Array<{ pais: Pais; dial: string }> = [];
  for (const pais of PAISES) {
    for (const dial of [pais.dial, ...(pais.alt ?? [])]) {
      if (!limpio.startsWith(dial)) continue;
      if (dial.length > mejorLargo) {
        mejorLargo = dial.length;
        candidatos = [{ pais, dial }];
      } else if (dial.length === mejorLargo) {
        candidatos.push({ pais, dial });
      }
    }
  }
  if (candidatos.length === 0) {
    return { country: undefined, dial: '', national: limpio.slice(1) };
  }
  const elegido = candidatos.find((c) => c.pais.code === preferido) ?? candidatos[0];
  return {
    country: elegido.pais.code,
    dial: elegido.dial,
    national: limpio.slice(elegido.dial.length),
  };
}

/** «+34600123456» → «+34 600123456». Lo que no se reconoce, tal cual. */
export function formatearTelefono(valor: string | undefined): string {
  if (!valor) return '';
  const partes = partirTelefono(valor);
  return partes.dial ? `${partes.dial} ${partes.national}` : valor;
}
